import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { formatYMD, formatTimeRange } from "@/lib/dates";
import Mandala from "@/components/Mandala";
import LastSpotsModule from "@/components/LastSpotsModule";
import SEO from "@/components/SEO";

const HOLLY = "/api/files/2954dd4f-d431-4797-a7c8-838938f7b6cb";
const HERO_BG = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/yipigdji_Holly%202014%20Passions%20Production%20Co-40.jpeg";
// BOOK cover placeholder — Holly can override via Admin → Page Images → Home
// → book-cover. Originally pointed to a WP-hosted Screenshot URL that died
// when avianayoga.com DNS was re-pointed. Swapped for a working Emergent
// artifact so the "Soaring With the Soul" section never renders a broken tile.
const BOOK_FALLBACK = "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/exlvccso_Holly%202014%20Passions%20Production%20Co-95.jpg";
const HOLLY_FALLBACK = HOLLY;
const PATH_IMAGES = {
  retreats: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/zbhwvo23_SEDONARETREAT-131.png",
  trainings: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/f2nrwkzu_Page89option.jpg",
  courses: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/exlvccso_Holly%202014%20Passions%20Production%20Co-95.jpg",
  events: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/ryjmg6la_IMG_2881.jpeg",
};

const PILLARS = [
  { key: "retreats", variant: "sun", title: "Sacred Retreats", body: "Immersive itineraries that thread movement, nature, equine wisdom, and rest into one transformational arc.", to: "/retreats" },
  { key: "trainings", variant: "star", title: "Teacher Trainings", body: "Fourteen years of teaching distilled into programs that initiate teachers — not merely certify them.", to: "/trainings" },
  { key: "courses", variant: "rose", title: "Online Courses", body: "Self-paced study with downloadable manuals, video lessons, and lifetime access to your library.", to: "/courses" },
  { key: "events", variant: "geo", title: "Sacred Events", body: "Sister Circles, moon ceremonies, sound healing, and Bliss Bell Tent gatherings.", to: "/events" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [images, setImages] = useState({});

  useEffect(() => {
    (async () => {
      // Defensive: each call is independent. If one fails, the others still
      // populate. Each setState only accepts arrays — prevents a runtime
      // "x.slice(...).map is not a function" crash if the backend or a CDN
      // returns a non-array payload (HTML error page, string, null, etc.).
      const asArray = (v) => (Array.isArray(v) ? v : []);
      try { setFeatured(asArray((await api.get("/offerings/featured")).data)); } catch (_) { /* keep [] */ }
      try { setEvents(asArray((await api.get("/offerings?type=event")).data)); } catch (_) { /* keep [] */ }
      try { setTestimonials(asArray((await api.get("/testimonials")).data)); } catch (_) { /* keep [] */ }
      try { setImages((await api.get("/page-images/home")).data?.slots || {}); } catch (_) { /* keep {} */ }
    })();
  }, []);

  const img = (key, fallback) => images[key]?.[0] || fallback;

  return (
    <div data-testid="home-page">
      <SEO
        title="Aviana Yoga — Aerial Yoga Teacher Training, Women's Retreats & Reiki with Holly Johnson"
        description="Yoga Alliance certified aerial yoga teacher trainings, women's healing retreats in Colorado, and Reiki energy healing certifications. Step into your sacred path with Holly Johnson — Aviana Yoga."
        path="/"
        image="/social/og-home.jpg"
        keywords="aerial yoga teacher training, aerial yoga certification, women's healing retreat, reiki energy healing, reiki certification, restorative aerial yoga, Holly Johnson, Aviana Yoga, YACEP, sister circle, Colorado women's retreat"
      />
      {/* ─── HERO — full-bleed plum sanctuary ─── */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden bg-aviana-plumDeep text-aviana-cream" data-testid="hero-section">
        {/* Full-bleed photo */}
        <img
          src={HERO_BG}
          alt="Holly suspended in aerial silks beneath autumn maples"
          className="absolute inset-0 w-full h-full object-cover object-[60%_center]"
        />
        {/* Plum overlay gradient — dominant on the left for copy, image breathes on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-aviana-plumDeep via-aviana-plumDeep/85 to-aviana-plumDeep/20 lg:from-aviana-plumDeep/95 lg:via-aviana-plumDeep/60 lg:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep/70 via-transparent to-aviana-plumDeep/40" />
        {/* Subtle grain */}
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        {/* Ornamental mandala — large, faded, behind copy */}
        <Mandala size={780} variant="full" className="absolute -left-32 -bottom-40 text-aviana-gold opacity-[0.07] hidden md:block animate-spin-slow" />

        <div className="container-aviana relative z-10 py-32 md:py-40">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-8 animate-fade-up" data-testid="hero-mandala-divider">
              <Mandala size={32} variant="lotus" className="text-aviana-goldSoft" />
              <p className="overline text-aviana-goldSoft">A sanctuary for the divine feminine</p>
            </div>

            <h1 className="font-display text-[64px] sm:text-[88px] lg:text-[120px] leading-[0.92] tracking-[-0.01em] animate-fade-up" data-testid="hero-headline">
              Sacred
              <br />
              Embodied
              <br />
              <span className="gold-text italic font-serif">Living.</span>
            </h1>

            <p className="mt-10 max-w-xl text-[19px] md:text-[21px] text-aviana-cream/85 leading-[1.65] font-serif italic animate-fade-up">
              <strong className="not-italic font-medium text-aviana-cream">Welcome home.</strong>{" "}
              A space for sisterhood, transformative yoga, empowering trainings, and soul-stirring
              retreats — held with deep care by <span className="text-aviana-goldSoft not-italic font-medium tracking-wide">Holly Johnson</span>.
            </p>

            <div className="mt-12 flex flex-wrap gap-4 animate-fade-up">
              <Link to="/retreats" className="btn-gold" data-testid="hero-cta-retreats">
                Discover Retreats <ArrowRight size={16} />
              </Link>
              <Link to="/trainings/aerial-yoga" className="px-8 py-3.5 rounded-full border border-aviana-goldSoft/50 text-aviana-goldSoft hover:bg-aviana-goldSoft hover:text-aviana-plumDeep text-sm tracking-[0.18em] uppercase transition-all duration-500 inline-flex items-center gap-2" data-testid="hero-cta-trainings">
                Receive a Training
              </Link>
            </div>

            <div className="mt-16 flex items-center gap-8 text-aviana-cream/60 text-[11px] tracking-[0.28em] uppercase animate-fade-up">
              <span>Guiding since 2014</span>
              <span className="w-8 h-px bg-aviana-goldSoft/40" />
              <span>3,000+ sisters held</span>
              <span className="w-8 h-px bg-aviana-goldSoft/40 hidden sm:block" />
              <span className="hidden sm:inline">50 Hour Aerial Yoga Teacher Training</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-aviana-goldSoft/70 animate-fade-up">
          <span className="text-[10px] tracking-[0.35em] uppercase">scroll · breathe</span>
          <div className="w-px h-12 bg-gradient-to-b from-aviana-goldSoft/80 to-transparent" />
        </div>
      </section>

      <div className="gold-divider container-aviana" />

      {/* ─── LAST SPOTS REMAINING — soft urgency module ─── */}
      <LastSpotsModule />

      {/* HOLLY INTRO — first-person, the voice behind the brand */}
      <section className="py-28 md:py-36" data-testid="holly-intro">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-5 relative">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-4 rounded-[50%] bg-gradient-to-br from-aviana-goldSoft via-aviana-gold to-aviana-goldDeep opacity-30 blur-2xl" />
              <div className="relative aspect-[3/4] overflow-hidden ring-[3px] ring-aviana-gold/40" style={{ borderRadius: "50%" }}>
                <img src={img("holly-portrait", HOLLY_FALLBACK)} alt="Holly Johnson, founder of Aviana" className="w-full h-full object-cover" style={{ transition: "transform 1.2s" }} />
              </div>
              <Mandala size={80} className="absolute -top-6 -right-2 text-aviana-gold opacity-70" />
              <Mandala size={48} variant="rose" className="absolute -bottom-2 -left-2 text-aviana-plum opacity-50" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-5">A note from Holly</p>
            <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
              The woman <em className="italic font-serif gold-text">behind the goddess.</em>
            </h2>
            <p className="mt-10 font-serif italic text-[24px] md:text-[28px] leading-[1.4] text-aviana-inkSoft">
              <strong className="not-italic font-medium text-aviana-ink">I didn't create Aviana.</strong>{" "}
              I <em>channeled</em> her — an archetype of mythical feminine energy who arrived to hold the container for what I was being called to step into.
            </p>
            <p className="mt-8 text-aviana-muted leading-[1.95] text-[17px]">
              For over <strong className="text-aviana-ink font-medium">fourteen years</strong> I've guided women into aerial silks, through the alchemy of sound, breath, and ceremony, and into the wisdom that horses, nature, and sisterhood reveal. Every offering I hold is designed <em className="text-aviana-ink not-italic font-medium">not as escape, but as homecoming</em> — to body, to breath, to the wilder version of yourself you may have set aside.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/about" className="btn-ghost-gold">Holly's Story</Link>
              <Link to="/aviana" className="text-sm tracking-[0.2em] uppercase text-aviana-muted hover:text-aviana-ink self-center">
                Meet Aviana →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MEET AVIANA — a short mythical ribbon linking to the full archetype page */}
      <section className="relative py-24 md:py-32 bg-aviana-plumDeep text-aviana-cream overflow-hidden" data-testid="aviana-ribbon">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        <Mandala size={620} variant="full" className="absolute -right-32 top-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.10] animate-spin-slow" />
        <div className="container-aviana relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-6 text-aviana-goldSoft">The archetype</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[1.02]">
              Meet <em className="italic font-serif gold-text">Aviana.</em>
            </h2>
            <p className="mt-8 font-serif italic text-[22px] md:text-[26px] leading-[1.45] text-aviana-cream/90 max-w-2xl">
              A <strong className="not-italic font-medium text-aviana-cream">galactic goddess</strong> born to the seven sisters of Pleiades — dignified, playful, rooted in Earth and dancing in moonlight.
            </p>
            <p className="mt-7 text-aviana-cream/75 leading-[1.85] max-w-2xl text-[17px]">
              Aviana is the living frequency woven through every retreat, every training, every breath we share. She is the steward of <em>sacred embodiment</em>, the keeper of <em>energetic intelligence</em>, and the invitation back into your own divine spark.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/aviana" className="btn-gold" data-testid="aviana-discover-cta">
                Discover Aviana <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] border border-aviana-gold/40 p-10 bg-aviana-plumDeep/60 backdrop-blur-sm">
              <p className="overline text-aviana-goldSoft mb-5">The four roots</p>
              <ul className="space-y-4 text-aviana-cream/85 text-[15px] leading-[1.6]">
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[6px] text-[8px]">◆</span><span><strong className="text-aviana-cream">Sacred Embodiment</strong> — movement &amp; breath as portals</span></li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[6px] text-[8px]">◆</span><span><strong className="text-aviana-cream">Energetic Intelligence</strong> — emotional fluidity, heart-centered presence</span></li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[6px] text-[8px]">◆</span><span><strong className="text-aviana-cream">Playful Discovery</strong> — curiosity &amp; wonder as guides</span></li>
                <li className="flex gap-3"><span className="text-aviana-goldSoft mt-[6px] text-[8px]">◆</span><span><strong className="text-aviana-cream">Community &amp; Support</strong> — sisterhood that uplifts</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR PATH — pillars */}
      <section className="py-24 md:py-32 bg-aviana-surface relative overflow-hidden" data-testid="pillars-section">
        <div className="container-aviana relative">
          <div className="grid md:grid-cols-12 gap-12 items-end mb-20">
            <div className="md:col-span-7">
              <p className="overline mb-5">Follow your bliss</p>
              <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
                Choose <em className="italic font-serif">your path.</em>
              </h2>
            </div>
            <p className="md:col-span-5 text-aviana-muted leading-[1.85] font-serif text-lg italic">
              Whether you arrive seeking certification, sisterhood, or simply space to breathe — Aviana offers carefully held <strong className="not-italic text-aviana-ink font-medium">containers</strong> designed to help you remember who you are.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p) => {
              return (
                <Link key={p.title} to={p.to} className="card-aviana p-7 group relative overflow-hidden" data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="relative w-32 h-32 mx-auto mb-5">
                    <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-aviana-goldSoft to-aviana-gold opacity-50 blur-md group-hover:opacity-80 transition-opacity" />
                    <div className="relative w-full h-full overflow-hidden rounded-full ring-2 ring-aviana-gold/50">
                      <img src={PATH_IMAGES[p.key]} alt={p.title} className="w-full h-full object-cover" style={{ transition: "transform 1.2s" }} />
                    </div>
                    {/* Mandala overlay icon — replaces generic Lucide pictogram */}
                    <div className="absolute -bottom-2 -right-2 bg-aviana-cream rounded-full p-2 text-aviana-plum ring-2 ring-aviana-gold/40 shadow-[0_4px_14px_-4px_rgba(90,42,91,0.25)] transition-transform duration-700 group-hover:rotate-[18deg]">
                      <Mandala size={36} variant={p.variant} strokeWidth={1.2} />
                    </div>
                  </div>
                  <h3 className="font-display text-[26px] text-aviana-ink mb-3 text-center">{p.title}</h3>
                  <p className="text-sm text-aviana-muted leading-[1.7] mb-6 text-center">{p.body}</p>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-aviana-gold group-hover:tracking-[0.28em] transition-all text-center">Learn more →</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED EVENTS — plum background to match the "For Studios" energy you loved */}
      {featured.length > 0 && (
        <section className="py-28 md:py-36 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden" data-testid="featured-events">
          <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
          <Mandala size={760} variant="full" className="absolute -right-32 -top-32 text-aviana-gold opacity-[0.08] animate-spin-slow" />
          <div className="container-aviana relative">
            <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
              <div>
                <p className="overline mb-5 text-aviana-goldSoft">Mark your calendar</p>
                <h2 className="font-display text-5xl md:text-7xl leading-[1.02]">
                  Coming <em className="italic font-serif gold-text">soon.</em>
                </h2>
              </div>
              <Link to="/retreats" className="text-[12px] tracking-[0.22em] uppercase text-aviana-goldSoft hover:text-aviana-cream self-end">
                View all offerings →
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featured.slice(0, 3).map((f) => {
                const dateStr = f.start_date ? formatYMD(f.start_date, { month: "long", day: "numeric" }) : f.date_label;
                const endStr = f.end_date ? formatYMD(f.end_date, { month: "short", day: "numeric", year: "numeric" }) : "";
                const isCritical = f.spots_left !== null && f.spots_left !== undefined && f.spots_left <= 3 && f.spots_left > 0;
                const linkTo = f.slug === "online-restorative-aerial-yoga-teacher-training"
                  ? "/courses/restorative-aerial-yoga-teacher-training"
                  : f.slug === "solstice-in-the-saddle-a-sacred-mountain-retreat-for-women"
                  ? "/retreats/solstice-in-the-saddle"
                  : f.slug === "aerial-yoga-teacher-training-st-augustine-florida" || f.slug === "aerial-yoga-teacher-training-the-pearl-columbia-md"
                  ? "/trainings/aerial-yoga"
                  : `/offering/${f.slug}`;
                const typeLabel = f.type === "retreat" ? "Retreat" : f.type === "training" ? "Teacher Training" : f.type === "course" ? "Online Course" : f.type;
                return (
                  <article key={f.id} className="group relative rounded-[2rem] overflow-hidden bg-aviana-plumDeep/40 border border-aviana-gold/30 flex flex-col hover:border-aviana-gold/60 transition-all" data-testid={`featured-${f.slug}`}>
                    <div className="aspect-[16/11] overflow-hidden">
                      <img src={f.image_url || "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/zbhwvo23_SEDONARETREAT-131.png"} alt={f.title} className="w-full h-full object-cover" style={{ transition: "transform 1.2s" }} />
                    </div>
                    <div className="p-7 md:p-8 flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <p className="overline text-aviana-goldSoft">{typeLabel}</p>
                        {isCritical && (
                          <span className="text-[10px] tracking-[0.22em] uppercase text-aviana-goldSoft bg-aviana-gold/10 border border-aviana-gold/40 px-3 py-1 rounded-full">
                            Only {f.spots_left} {f.spots_left === 1 ? "spot" : "spots"} left
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-2xl md:text-[28px] text-aviana-cream leading-[1.15]">{f.title}</h3>
                      {f.exclusive_to_studio && (
                        <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-aviana-goldSoft" data-testid={`featured-exclusive-${f.slug}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-aviana-goldSoft" />
                          Exclusive · {f.exclusive_studio_name || "Studio instructors only"}
                        </p>
                      )}
                      {dateStr && (
                        <div className="mt-4 flex items-center gap-2 text-[12px] text-aviana-cream/70 flex-wrap">
                          <span className="font-serif italic">{dateStr}{endStr && f.end_date !== f.start_date ? ` — ${endStr}` : ""}</span>
                          {formatTimeRange(f.start_time, f.end_time) && (
                            <>
                              <span className="text-aviana-goldSoft">·</span>
                              <span data-testid={`featured-time-${f.slug}`}>{formatTimeRange(f.start_time, f.end_time)}</span>
                            </>
                          )}
                          {f.location && <><span className="text-aviana-goldSoft">·</span><span>{f.location}</span></>}
                        </div>
                      )}
                      {f.subtitle && (
                        <p className="mt-4 text-aviana-cream/75 leading-[1.7] text-[14px]">{f.subtitle}</p>
                      )}
                      <div className="mt-auto pt-7 flex items-center justify-between gap-3">
                        <Link to={linkTo} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid={`featured-cta-${f.slug}`}>
                          Reserve · ${f.deposit_usd || f.price_usd}{f.deposit_usd && " dep"}
                        </Link>
                        <Link to={linkTo} className="text-[10px] tracking-[0.22em] uppercase text-aviana-goldSoft hover:text-aviana-cream">
                          Details →
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* HOST A TRAINING — section removed from home; lives on /host-a-training */}

      {/* ONLINE COURSE — feature now happens via Featured Events; standalone section removed */}

      {/* BOOK + PROMISE BAND */}
      <section className="py-28 md:py-36 bg-aviana-surface" data-testid="book-band">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 lg:order-2">
            <p className="overline-plum mb-5">Soaring With the Soul</p>
            <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
              The Aviana <em className="italic font-serif gold-text">Aerial Yoga Guide.</em>
            </h2>
            <p className="mt-7 text-aviana-muted leading-[1.85] max-w-xl">
              Over 100 blissful aerial yoga poses — from grounding basics to exhilarating flows.
              Now in its devotedly refined second edition.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="https://amzn.to/4ttObU4" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="book-cta">
                <BookOpen size={16} /> Order on Amazon
              </a>
              <Link to="/resources" className="btn-secondary">More resources</Link>
            </div>
            <p className="mt-6 text-[11px] tracking-[0.18em] uppercase text-aviana-muted/70">As an Amazon Associate I earn from qualifying purchases.</p>
          </div>
          <div className="lg:col-span-5 lg:order-1">
            <div className="img-zoom aspect-[3/4] rounded-[2rem] ring-1 ring-aviana-gold/30 max-w-md mx-auto">
              <img src={img("book-cover", BOOK_FALLBACK)} alt="Soaring With the Soul book cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SACRED EVENTS — horizontal scroll carousel with calendar link */}
      {events.length > 0 && (
        <section className="py-28 md:py-36 bg-aviana-surface" data-testid="events-section">
          <div className="container-aviana mb-10">
            <div className="flex items-end justify-between flex-wrap gap-6">
              <div>
                <p className="overline mb-5">Local gatherings</p>
                <h2 className="font-display text-5xl md:text-7xl text-aviana-ink leading-[1.02]">
                  Sacred <em className="italic font-serif gold-text">events.</em>
                </h2>
                <p className="mt-6 max-w-xl text-aviana-muted leading-[1.85] font-serif italic text-[18px]">
                  Sister Circles, moon ceremonies, sound healing, &amp; aerial gatherings — <em>scroll</em> to see what's coming.
                </p>
              </div>
              <Link to="/events" className="text-[12px] tracking-[0.22em] uppercase text-aviana-gold hover:text-aviana-goldDeep self-end" data-testid="view-calendar-link">
                View full calendar →
              </Link>
            </div>
          </div>
          {/* Horizontal scroll row */}
          <div className="relative">
            <div
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory pl-[max(2rem,calc((100vw-1280px)/2))] pr-8 pb-6 scrollbar-thin"
              data-testid="events-scroll-row"
              style={{ scrollbarWidth: "thin" }}
            >
              {events.map((ev) => {
                const dt = ev.start_date ? formatYMD(ev.start_date, { month: "short", day: "numeric" }) : (ev.date_label || "");
                const evTime = formatTimeRange(ev.start_time, ev.end_time);
                return (
                  <Link
                    key={ev.id}
                    to={`/offering/${ev.slug}`}
                    className="group shrink-0 w-[320px] md:w-[360px] snap-start rounded-[1.75rem] overflow-hidden bg-aviana-cream border border-aviana-border hover:border-aviana-gold/50 transition-all"
                    data-testid={`event-card-${ev.slug}`}
                  >
                    <div className="aspect-[5/4] overflow-hidden">
                      <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" style={{ transition: "transform 1.4s" }} />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <Mandala size={18} variant="lotus" className="text-aviana-gold" />
                        <p className="overline-plum text-[10px]">
                          {dt}{evTime && <span className="text-aviana-muted"> · {evTime}</span>}
                        </p>
                      </div>
                      <h3 className="font-display text-[22px] text-aviana-ink leading-[1.2]">{ev.title}</h3>
                      {ev.location && <p className="mt-3 text-[12px] tracking-[0.15em] uppercase text-aviana-muted/80">{ev.location}</p>}
                      <p className="mt-5 text-[10px] tracking-[0.22em] uppercase text-aviana-gold group-hover:text-aviana-goldDeep transition-colors">Reserve a spot →</p>
                    </div>
                  </Link>
                );
              })}
              {/* Calendar tile — last item nudges them to the full view */}
              <Link
                to="/events"
                className="shrink-0 w-[320px] md:w-[360px] snap-start rounded-[1.75rem] bg-aviana-plumDeep text-aviana-cream p-8 flex flex-col items-center justify-center text-center hover:bg-aviana-plum transition-all relative overflow-hidden"
                data-testid="event-card-calendar"
              >
                <Mandala size={120} variant="full" className="absolute -bottom-6 -right-6 text-aviana-gold opacity-[0.15] animate-spin-slow" />
                <p className="overline mb-5 text-aviana-goldSoft relative">Browse all</p>
                <h3 className="font-display text-3xl leading-[1.1] relative">See the full <em className="italic font-serif gold-text">calendar.</em></h3>
                <p className="mt-5 text-[12px] tracking-[0.22em] uppercase text-aviana-goldSoft relative">All upcoming events →</p>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS — compact, refined */}
      {testimonials.length > 0 && (
        <section className="py-24 md:py-28 bg-aviana-surface" data-testid="testimonials">
          <div className="container-aviana">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <p className="overline mb-3">From the Sisterhood</p>
                <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">
                  A practice that <em className="italic font-serif gold-text">holds you home.</em>
                </h2>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.slice(0, 4).map((t) => (
                <figure key={t.id} className="bg-aviana-cream rounded-2xl p-7 border border-aviana-border relative">
                  <Mandala size={22} variant="lotus" className="text-aviana-gold mb-4 opacity-70" />
                  <blockquote className="font-serif text-base leading-[1.55] text-aviana-inkSoft italic">"{t.quote.length > 180 ? t.quote.slice(0, 175) + "…" : t.quote}"</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-aviana-border/70">
                    <p className="font-display text-base text-aviana-ink">{t.name}</p>
                    <p className="text-[10px] tracking-[0.18em] uppercase text-aviana-muted/80 mt-0.5">{t.location}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA — full-bleed plum invocation */}
      <section className="py-32 md:py-48 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden" data-testid="closing-cta">
        <div className="absolute inset-0 grain pointer-events-none opacity-70" aria-hidden />
        <Mandala size={900} variant="full" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-aviana-gold opacity-[0.10] animate-spin-slow" />
        <div className="container-aviana relative text-center max-w-3xl mx-auto">
          <p className="overline mb-7 text-aviana-goldSoft">Your invitation</p>
          <h2 className="font-display text-5xl md:text-8xl leading-[0.98]">
            Unfurl your wings
            <br />
            <em className="italic font-serif gold-text">&amp; shine your light.</em>
          </h2>
          <p className="mt-10 text-aviana-cream/85 leading-[1.85] font-serif italic text-[19px] md:text-[22px]">
            <strong className="not-italic font-medium text-aviana-cream">Magnify your purpose.</strong>{" "}
            Expand what you offer. Step into the next chapter with sisters who see you.
          </p>
          <div className="mt-14 flex flex-wrap gap-4 justify-center">
            <Link to="/signup" className="btn-gold" data-testid="closing-signup">Join the Sisterhood</Link>
            <Link to="/retreats" className="text-[12px] tracking-[0.22em] uppercase text-aviana-goldSoft hover:text-aviana-cream self-center">Browse retreats →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
